import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import EvidenciaGaleria from '../components/ordenesTrabajo/EvidenciaGaleria';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';
import TextareaConDictado from '../components/common/TextareaConDictado';
import { calcularDuracionMinutos, formatDuration, formatDurationSeconds } from '../utils/timeHelpers';
import ROUTES from '../constants/routes';
import ConfirmDialog from '../components/common/ConfirmDialog';
import useConfirmAction from '../hooks/useConfirmAction';
import '../styles/pages/detalle-ot.css';
import '../styles/pages/ejecucion-actividad.css';

/**
 * Pagina de ejecucion de actividad individual.
 * Layout con sidebar derecha (stepper + progreso) y area principal
 * con header, evidencia, observaciones y barra de acciones.
 *
 * @returns {JSX.Element}
 */
function EjecucionActividad() {
  const { id, actividadId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  const [ot, setOt] = useState(null);
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef(null);

  // Observaciones
  const [observaciones, setObservaciones] = useState('');

  // Omitir
  const [showOmitirForm, setShowOmitirForm] = useState(false);
  const [omitirMotivo, setOmitirMotivo] = useState('');

  const iniciar = useConfirmAction();
  const completar = useConfirmAction();

  // Loading para omitir (inline form, no dialog)
  const [omitirLoading, setOmitirLoading] = useState(false);

  // ── Data fetching ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [otRes, actRes] = await Promise.all([
        apiService.getOrdenTrabajoById(id),
        apiService.getActividadesOrdenTrabajo(id)
      ]);

      if (!otRes.success) {
        setError(otRes.message || 'Orden de trabajo no encontrada');
        return;
      }
      setOt(otRes.data);
      if (actRes.success) {
        setActividades(actRes.data || []);
      }
    } catch {
      setError('Error al cargar datos');
      showToast('Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Re-fetch al cambiar de actividad dentro de la misma OT
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [fetchData, actividadId]);

  // Actividad actual
  const actividad = actividades.find(a => a.id === parseInt(actividadId));
  const actIndex = actividades.findIndex(a => a.id === parseInt(actividadId));

  // Inicializar observaciones al cargar actividad
  useEffect(() => {
    if (actividad) {
      setObservaciones(actividad.observaciones || '');
      setShowOmitirForm(false);
      setOmitirMotivo('');
    }
  }, [actividad?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Timer live (cada segundo) ──
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (!actividad?.fecha_inicio || actividad.estado !== 'en_progreso') {
      setElapsedSeconds(0);
      return;
    }

    const inicio = new Date(actividad.fecha_inicio);
    const calcSeconds = () => Math.max(0, Math.floor((Date.now() - inicio.getTime()) / 1000));
    setElapsedSeconds(calcSeconds());

    timerRef.current = setInterval(() => setElapsedSeconds(calcSeconds()), 1000);
    return () => clearInterval(timerRef.current);
  }, [actividad?.fecha_inicio, actividad?.estado]);


  // ── Acciones ──
  const handleConfirmCompletar = async () => {
    completar.setLoading(true);
    try {
      const data = { estado: 'completado' };
      if (observaciones.trim()) data.observaciones = observaciones.trim();
      const res = await apiService.cambiarEstadoActividad(id, actividadId, data);
      if (res.success) {
        setActividades(prev => prev.map(a =>
          a.id === parseInt(actividadId) ? { ...a, estado: 'completado', fecha_completado: new Date().toISOString() } : a
        ));
        showToast(res.message || 'Actividad completada', 'success');
        completar.reset();
        navigateToNext();
      } else {
        showToast(res.message || 'Error al completar', 'error');
        completar.setLoading(false);
      }
    } catch (err) {
      showToast(err.message || 'Error al completar actividad', 'error');
      completar.setLoading(false);
    }
  };

  const handleOmitir = async () => {
    if (!omitirMotivo.trim()) return;
    setOmitirLoading(true);
    try {
      const res = await apiService.cambiarEstadoActividad(id, actividadId, {
        estado: 'omitido',
        observaciones: omitirMotivo.trim()
      });
      if (res.success) {
        setActividades(prev => prev.map(a =>
          a.id === parseInt(actividadId) ? { ...a, estado: 'omitido', observaciones: omitirMotivo.trim() } : a
        ));
        showToast(res.message || 'Actividad omitida', 'success');
        setShowOmitirForm(false);
        setOmitirMotivo('');
        navigateToNext();
      } else {
        showToast(res.message || 'Error al omitir', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error al omitir actividad', 'error');
    } finally {
      setOmitirLoading(false);
    }
  };

  const navigateToNext = () => {
    const siguiente = actividades.find((a, i) =>
      i > actIndex && (a.estado === 'pendiente' || a.estado === 'en_progreso')
    );
    if (!siguiente) {
      navigate(ROUTES.MIS_ORDENES_TRABAJO_DETALLE.replace(':id', id));
      return;
    }
    if (siguiente.estado === 'pendiente') {
      iniciar.open(siguiente);
    } else {
      navigate(ROUTES.MIS_ORDENES_TRABAJO_ACTIVIDAD
        .replace(':id', id)
        .replace(':actividadId', siguiente.id)
      );
    }
  };

  const navigateToActividad = (act) => {
    if (act.estado === 'pendiente') {
      iniciar.open(act);
    } else {
      navigate(ROUTES.MIS_ORDENES_TRABAJO_ACTIVIDAD
        .replace(':id', id)
        .replace(':actividadId', act.id)
      );
    }
  };

  const handleConfirmIniciar = async () => {
    const act = iniciar.state.item;
    iniciar.setLoading(true);
    try {
      const response = await apiService.cambiarEstadoActividad(id, act.id, { estado: 'en_progreso' });
      if (response.success) {
        showToast('Actividad iniciada', 'success');
        iniciar.reset();
        navigate(ROUTES.MIS_ORDENES_TRABAJO_ACTIVIDAD
          .replace(':id', id)
          .replace(':actividadId', act.id)
        );
      } else {
        showToast(response.message || 'Error al iniciar actividad', 'error');
        iniciar.setLoading(false);
      }
    } catch {
      showToast('Error al iniciar la actividad', 'error');
      iniciar.setLoading(false);
    }
  };

  // ── Helpers ──
  const getDuracion = (act) => {
    if (act.estado === 'completado' && act.fecha_inicio) {
      return formatDuration(calcularDuracionMinutos(act.fecha_inicio, act.fecha_completado));
    }
    if (act.estado === 'en_progreso' && act.fecha_inicio) {
      return formatDuration(calcularDuracionMinutos(act.fecha_inicio, new Date()));
    }
    return '\u2014';
  };

  const getStepState = (act) => {
    if (act.id === parseInt(actividadId)) return 'active';
    if (act.estado === 'completado') return 'completed';
    if (act.estado === 'omitido') return 'omitido';
    return 'pending';
  };

  // Progreso
  const completadas = actividades.filter(a => a.estado === 'completado').length;
  const total = actividades.length;
  const pct = total > 0 ? Math.round((completadas / total) * 100) : 0;

  const equipoNombre = ot ? [ot.equipo?.marca, ot.equipo?.modelo].filter(Boolean).join(' ') : '';
  const isEditable = actividad && (actividad.estado === 'pendiente' || actividad.estado === 'en_progreso');

  // ── Render ──
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="main-content">
          <div className="wf-loading">
            <i className="fas fa-spinner fa-spin" /> Cargando actividad...
          </div>
        </div>
      </>
    );
  }

  if (error || !ot || !actividad) {
    return (
      <>
        <Navbar />
        <div className="main-content">
          <div className="wf-error">
            <div className="wf-error-icon"><i className="fas fa-clipboard-question" /></div>
            <p>{error || 'Actividad no encontrada'}</p>
            <button className="btn btn-primary" onClick={() => navigate(ROUTES.MIS_ORDENES_TRABAJO_DETALLE.replace(':id', id))}>
              <i className="fas fa-arrow-left" /> Volver a la OT
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="main-content">

        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Mis OTs', to: ROUTES.MIS_ORDENES_TRABAJO },
          { label: ot.codigo_ot, to: ROUTES.MIS_ORDENES_TRABAJO_DETALLE.replace(':id', id) },
          { label: `Actividad ${actividad.orden}` }
        ]} />

        <div className="wf-exec-layout">

          {/* ════════ MAIN CONTENT ════════ */}
          <div className="wf-exec-main">

            {/* Activity Header */}
            <div className="wf-card wf-exec-activity-header">
              <div className="wf-card-body">
                <div className="wf-exec-header-inner">
                  <div className="wf-exec-badge">{String(actividad.orden).padStart(2, '0')}</div>
                  <div className="wf-exec-header-body">
                    <div className="wf-exec-supra">
                      Actividad {actividad.orden} de {total} · {equipoNombre}
                    </div>
                    <div className="wf-exec-title">{actividad.nombre}</div>
                    {actividad.descripcion && (
                      <div className="wf-exec-desc">{actividad.descripcion}</div>
                    )}
                  </div>
                  {actividad.estado === 'en_progreso' && actividad.fecha_inicio && (
                    <div className="wf-exec-timer">
                      <div className="wf-exec-timer-label">Tiempo transcurrido</div>
                      <div className="wf-exec-timer-value">{formatDurationSeconds(elapsedSeconds)}</div>
                      <div>
                        <span className="wf-exec-timer-badge">
                          <i className="fas fa-circle fa-xs" /> En ejecucion
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Status banner para actividades finalizadas */}
            {actividad.estado === 'completado' && (
              <div className="alert alert-success-light">
                <i className="fas fa-check-circle" />
                <div>
                  <strong>Actividad completada</strong>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
                    {actividad.fecha_completado && new Date(actividad.fecha_completado).toLocaleString('es-CL')}
                    {actividad.fecha_inicio && actividad.fecha_completado &&
                      ` · Duracion: ${formatDuration(calcularDuracionMinutos(actividad.fecha_inicio, actividad.fecha_completado))}`
                    }
                  </div>
                </div>
              </div>
            )}
            {actividad.estado === 'omitido' && (
              <div className="alert alert-warning">
                <i className="fas fa-forward" />
                <div>
                  <strong>Actividad omitida</strong>
                  {actividad.observaciones && (
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
                      Motivo: {actividad.observaciones}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Evidencia Fotografica */}
            <div className="wf-card">
              <div className="wf-card-header">
                <div className="wf-card-header-icon yellow">
                  <i className="fas fa-camera" />
                </div>
                <span className="wf-card-title">Evidencia Fotografica</span>
              </div>
              <div className="wf-card-body">
                <EvidenciaGaleria
                  actividadId={actividad.id}
                  disabled={!isEditable}
                  onEvidenciaChange={fetchData}
                  canEdit={hasPermission('evidencia_actividad.update')}
                  canDelete={hasPermission('evidencia_actividad.delete')}
                />
              </div>
            </div>

            {/* Observaciones */}
            <div className="wf-card">
              <div className="wf-card-header">
                <div className="wf-card-header-icon blue">
                  <i className="fas fa-file-alt" />
                </div>
                <span className="wf-card-title">Observaciones</span>
              </div>
              <div className="wf-card-body">
                <TextareaConDictado
                  value={observaciones}
                  onChange={setObservaciones}
                  placeholder="Describe hallazgos, anomalías o notas técnicas..."
                  disabled={!isEditable}
                />
              </div>
            </div>

            {/* Action Bar */}
            {isEditable && hasPermission('actividades.execute') && (
              <div className="wf-exec-action-bar" role="toolbar" aria-label="Acciones de actividad">
                {showOmitirForm ? (
                  <div className="wf-exec-omitir-form">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Motivo de omision..."
                      value={omitirMotivo}
                      onChange={(e) => setOmitirMotivo(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && omitirMotivo.trim()) handleOmitir();
                        if (e.key === 'Escape') { setShowOmitirForm(false); setOmitirMotivo(''); }
                      }}
                      autoFocus
                    />
                    <button className="btn btn-secondary" onClick={handleOmitir} disabled={!omitirMotivo.trim() || omitirLoading}>
                      {omitirLoading
                        ? <><i className="fas fa-spinner fa-spin" /> Procesando...</>
                        : <><i className="fas fa-forward" /> Confirmar</>
                      }
                    </button>
                    <button className="btn btn-secondary" onClick={() => { setShowOmitirForm(false); setOmitirMotivo(''); }}>
                      <i className="fas fa-times" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="wf-exec-action-left">
                      <button className="btn btn-secondary" onClick={() => setShowOmitirForm(true)}>
                        <i className="fas fa-forward" /> Omitir actividad
                      </button>
                    </div>
                    <button className="btn btn-primary" onClick={() => completar.open(actividad)}>
                      <i className="fas fa-check" /> Completar actividad
                    </button>
                  </>
                )}
              </div>
            )}

          </div>

          {/* ════════ SIDEBAR ════════ */}
          <aside className="wf-exec-sidebar" aria-label="Progreso de actividades">
            <div className="wf-card">
              <div className="wf-card-header">
                <div className="wf-card-header-icon green">
                  <i className="fas fa-clipboard-list" />
                </div>
                <span className="wf-card-title">Actividades OT</span>
                <span className="wf-card-header-summary">{completadas} / {total} completadas</span>
              </div>

              {/* Progress bar — clases del styleguide */}
              <div className="wf-progress-block">
                <div className="wf-progress-header">
                  <span className="wf-progress-label">Progreso</span>
                  <span className="wf-progress-pct">{pct}%</span>
                </div>
                <div className="mant-progress-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                  <div className="mant-progress-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>

              {/* Stepper */}
              <ul className="wf-exec-stepper" aria-label="Lista de actividades">
                {actividades.map(act => {
                  const state = getStepState(act);
                  return (
                    <li
                      key={act.id}
                      className={`wf-exec-stepper-item ${state}`}
                      onClick={() => navigateToActividad(act)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigateToActividad(act); }}
                      aria-current={state === 'active' ? 'step' : undefined}
                    >
                      <div className={`wf-exec-step-circle ${state}`}>
                        {state === 'completed' ? <i className="fas fa-check" /> : String(act.orden).padStart(2, '0')}
                      </div>
                      <div className="wf-exec-step-info">
                        <div className={`wf-exec-step-name ${state}`}>{act.nombre}</div>
                        <div className={`wf-exec-step-duration ${state}`}>{getDuracion(act)}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

        </div>
      </div>

      <ConfirmDialog
        isOpen={iniciar.state.open}
        title="Iniciar Actividad"
        message={
          iniciar.state.item
            ? `¿Deseas iniciar la actividad "${iniciar.state.item.nombre}"? Se registrará la hora de inicio.`
            : ''
        }
        confirmText="Iniciar"
        onConfirm={handleConfirmIniciar}
        onCancel={iniciar.close}
        variant="info"
        loading={iniciar.state.loading}
      />

      <ConfirmDialog
        isOpen={completar.state.open}
        title="Completar Actividad"
        message="Al completar esta actividad no podra volver a editarla. ¿Desea continuar?"
        confirmText="Completar"
        onConfirm={handleConfirmCompletar}
        onCancel={completar.close}
        variant="warning"
        loading={completar.state.loading}
      />
    </>
  );
}

export default EjecucionActividad;
